/**
 * Betting odds adapters — convert canonical data-layer types to UI-friendly shapes.
 */

import { MatchOddsMarketEnum } from "fansunited-sdk-esm";
import type { FUSportsBettingOperatorOdds, getEventsOddsByDateRange } from "fansunited-data-layer";
import type { OddsData, BookmakerOdds, MatchOddsMap, MarketType } from "./types";

// The event-odds tree isn't re-exported as named types from the top-level
// entry (only the option types are). Derive them from the exported function
// signature so we don't need a deep import or another data-layer bump.
type EventsOddsPage = Awaited<ReturnType<typeof getEventsOddsByDateRange>>;
export type FUSportsEventOdds = EventsOddsPage["data"][number];
type FUSportsEventOperatorOdds = FUSportsEventOdds["operators"][number];

/**
 * Canonical order for selections within each market. Sportal365 returns
 * selections in unstable order (1x2 may arrive as 1/X/2, X/1/2, 2/X/1, …);
 * sort them so the UI always renders home/draw/away, over/under, etc.
 * Keys are uppercased market codes; inner keys are uppercased selection codes.
 */
const CANONICAL_SELECTION_ORDER: Record<string, Record<string, number>> = {
    FT_1X2: { "1": 0, HOME: 0, X: 1, DRAW: 1, "2": 2, AWAY: 2 },
    HT_1X2: { "1": 0, HOME: 0, X: 1, DRAW: 1, "2": 2, AWAY: 2 },
    DOUBLE_CHANCE: { "1X": 0, HOME_OR_DRAW: 0, "12": 1, HOME_OR_AWAY: 1, X2: 2, DRAW_OR_AWAY: 2 },
    BOTH_TEAMS_SCORE: { YES: 0, NO: 1 },
    // 2-way winner markets used by basketball / american football / ice hockey / tennis.
    WINNER_OT: { "1": 0, HOME: 0, "2": 1, AWAY: 1 },
    WINNER_OT_PEN: { "1": 0, HOME: 0, "2": 1, AWAY: 1 },
    WINNER: { "1": 0, HOME: 0, "2": 1, AWAY: 1 },
};

function selectionRank(marketCode: string, selectionCode: string): number {
    const market = marketCode.toUpperCase();
    const selection = selectionCode.toUpperCase();

    const map = CANONICAL_SELECTION_ORDER[market];
    if (map && selection in map) return map[selection];

    // OVER_GOALS_* markets: over before under
    if (market.startsWith("OVER_GOALS_")) {
        if (selection === "OVER") return 0;
        if (selection === "UNDER") return 1;
    }

    return Number.MAX_SAFE_INTEGER;
}

/**
 * Return a copy of `operators` with each market's selections sorted into a
 * canonical order (see CANONICAL_SELECTION_ORDER). Selections with unknown
 * codes fall to the end in their original relative order.
 */
export function withSortedSelections(operators: FUSportsBettingOperatorOdds[]): FUSportsBettingOperatorOdds[] {
    return operators.map((op) => ({
        ...op,
        markets: op.markets.map((market) => {
            const marketCode = market.type.code;
            const withIndex = market.selections.map((sel, idx) => ({ sel, idx }));
            withIndex.sort((a, b) => {
                const rankDiff = selectionRank(marketCode, a.sel.code) - selectionRank(marketCode, b.sel.code);
                return rankDiff !== 0 ? rankDiff : a.idx - b.idx;
            });
            return { ...market, selections: withIndex.map((w) => w.sel) };
        }),
    }));
}

/**
 * Bridge the new event-shaped odds tree (returned by
 * `getEventsOddsByDateRange` / `getEventOddsByMatchId`) to the legacy
 * `FUSportsBettingOperatorOdds` shape that every downstream consumer already
 * understands (`toBookmakerOdds`, `toMatchOddsMap`, `withSortedSelections`).
 *
 * The events endpoint doesn't split `PRE_EVENT` vs `LIVE`, so we tag every
 * bridged record as `PRE_EVENT` — LIVE-only consumers can key off the match
 * status separately.
 */
export function eventOddsToLegacyOperators(event: FUSportsEventOdds): FUSportsBettingOperatorOdds[] {
    return event.operators.map(bridgeOperator);
}

function bridgeOperator(src: FUSportsEventOperatorOdds): FUSportsBettingOperatorOdds {
    const op = src.operator;
    const firstAsset = op.assets?.[0];
    const logo = firstAsset?.transparentBackgroundUrl || firstAsset?.logo;

    return {
        operator: {
            id: op.id,
            name: op.name,
            url: src.urls?.desktop ?? undefined,
            branding: {
                backgroundColor: op.branding?.backgroundColor,
                textColor: op.branding?.textColor,
                logo,
            },
        },
        type: "PRE_EVENT",
        markets: src.markets.map((market) => ({
            type: { id: market.market, code: market.market, name: market.market },
            period: { id: market.scope, type: market.scope, name: market.scope },
            selections: market.selections.map((sel) => ({
                id: sel.operatorInfo?.selectionId ?? sel.code,
                // `name` is what the UI renders as the outcome label — must be the
                // machine code ("1", "X", "2", "Over", …). The event-shape's
                // `value` is human-readable and, for 1x2, resolves to the team
                // name — using it here would print the team names in the odds
                // buttons.
                name: sel.code,
                code: sel.code,
                value: sel.value ?? undefined,
                odds: typeof sel.odds === "number" ? sel.odds.toString() : sel.odds,
                oddsOld: sel.oddsOld != null ? String(sel.oddsOld) : undefined,
                movement:
                    sel.movement === "UP" ? "UP" : sel.movement === "DOWN" ? "DOWN" : "NONE",
                url: sel.urls?.desktop ?? undefined,
                providerSelectionId: sel.operatorInfo?.selectionId,
            })),
        })),
    };
}

/**
 * Canonical batch match odds type.
 * Re-declared locally because the data-layer package doesn't re-export it from the main entry point.
 */
type FUSportsBatchMatchOdds = Map<string, FUSportsBettingOperatorOdds[]>;

// ---------------------------------------------------------------------------
// Canonical → UI adapters
// ---------------------------------------------------------------------------

/**
 * Convert a single canonical `FUSportsBettingOperatorOdds` to the
 * UI-friendly `BookmakerOdds` shape.
 *
 * Uses the first market's selections (matching the old custom hook behaviour).
 */
export function toBookmakerOdds(src: FUSportsBettingOperatorOdds): BookmakerOdds | null {
    if (!src.operator || src.markets.length === 0) return null;

    const market = src.markets[0];

    const odds: OddsData[] = market.selections.map((sel) => {
        let change: OddsData["change"] = "none";
        if (sel.movement === "UP") change = "up";
        else if (sel.movement === "DOWN") change = "down";

        return {
            id: sel.id,
            providerSelectionId: sel.providerSelectionId,
            label: sel.name,
            value: parseFloat(sel.odds).toFixed(2),
            url: sel.url,
            change,
        };
    });

    const op = src.operator;

    return {
        odds,
        operator: {
            name: op.name,
            url: op.url ?? "",
            ...(op.branding?.logo && {
                logo: { url: op.branding.logo, alt: `${op.name} logo` },
            }),
            backgroundColor: op.branding?.backgroundColor,
            textColor: op.branding?.textColor,
        },
    };
}

/**
 * Convert a full canonical `FUSportsBatchMatchOdds` map to the
 * UI-friendly `MatchOddsMap`.
 */
export function toMatchOddsMap(src: FUSportsBatchMatchOdds): MatchOddsMap {
    const result: MatchOddsMap = new Map();

    src.forEach((operators, matchId) => {
        const converted = operators.map(toBookmakerOdds).filter((b): b is BookmakerOdds => b !== null);

        if (converted.length > 0) {
            result.set(matchId, converted);
        }
    });

    return result;
}

/**
 * Maps a MarketType to the SDK's MatchOddsMarketEnum
 *
 * @param market - The market type
 * @returns The corresponding MatchOddsMarketEnum value
 */
export function getMarketEnum(market: MarketType): MatchOddsMarketEnum {
    switch (market) {
        case "FT_1X2":
            return MatchOddsMarketEnum.FT_1X2;
        case "DOUBLE_CHANCE":
            return MatchOddsMarketEnum.DOUBLE_CHANCE;
        case "HT_FT":
            return MatchOddsMarketEnum.HT_FT;
        case "BOTH_TEAMS_SCORE":
            return MatchOddsMarketEnum.BOTH_TEAMS_SCORE;
        case "CORRECT_SCORE":
            return MatchOddsMarketEnum.CORRECT_SCORE;
        case "PLAYER_SCORE_FIRST_GOAL":
            return MatchOddsMarketEnum.PLAYER_SCORE_FIRST_GOAL;
        case "PLAYER_SCORE":
            return MatchOddsMarketEnum.PLAYER_SCORE;
        case "PLAYER_RED_CARD":
            return MatchOddsMarketEnum.PLAYER_RED_CARD;
        case "PLAYER_YELLOW_CARD":
            return MatchOddsMarketEnum.PLAYER_YELLOW_CARD;
        case "OVER_GOALS_0_5":
            return MatchOddsMarketEnum.OVER_GOALS_0_5;
        case "OVER_GOALS_1_5":
            return MatchOddsMarketEnum.OVER_GOALS_1_5;
        case "OVER_GOALS_2_5":
            return MatchOddsMarketEnum.OVER_GOALS_2_5;
        case "OVER_GOALS_3_5":
            return MatchOddsMarketEnum.OVER_GOALS_3_5;
        case "OVER_GOALS_4_5":
            return MatchOddsMarketEnum.OVER_GOALS_4_5;
        case "OVER_GOALS_5_5":
            return MatchOddsMarketEnum.OVER_GOALS_5_5;
        default:
            return MatchOddsMarketEnum.FT_1X2;
    }
}

/**
 * Market type object returned by the API
 */
interface ApiMarketType {
    id?: string;
    name?: string;
    code?: string;
    entityType?: string;
}

/**
 * Maps the API market type object back to our MarketType string
 *
 * @param marketType - The market type object from API (or SDK enum for backward compatibility)
 * @returns The corresponding market type string, or null if not recognized
 */
export function getMarketTypeFromEnum(marketType: MatchOddsMarketEnum | ApiMarketType): MarketType | null {
    // Handle SDK enum values (backward compatibility)
    if (typeof marketType === "string") {
        switch (marketType) {
            case MatchOddsMarketEnum.FT_1X2:
                return "FT_1X2";
            case MatchOddsMarketEnum.DOUBLE_CHANCE:
                return "DOUBLE_CHANCE";
            case MatchOddsMarketEnum.HT_FT:
                return "HT_FT";
            case MatchOddsMarketEnum.BOTH_TEAMS_SCORE:
                return "BOTH_TEAMS_SCORE";
            case MatchOddsMarketEnum.CORRECT_SCORE:
                return "CORRECT_SCORE";
            case MatchOddsMarketEnum.PLAYER_SCORE_FIRST_GOAL:
                return "PLAYER_SCORE_FIRST_GOAL";
            case MatchOddsMarketEnum.PLAYER_SCORE:
                return "PLAYER_SCORE";
            case MatchOddsMarketEnum.PLAYER_RED_CARD:
                return "PLAYER_RED_CARD";
            case MatchOddsMarketEnum.PLAYER_YELLOW_CARD:
                return "PLAYER_YELLOW_CARD";
            case MatchOddsMarketEnum.OVER_GOALS_0_5:
                return "OVER_GOALS_0_5";
            case MatchOddsMarketEnum.OVER_GOALS_1_5:
                return "OVER_GOALS_1_5";
            case MatchOddsMarketEnum.OVER_GOALS_2_5:
                return "OVER_GOALS_2_5";
            case MatchOddsMarketEnum.OVER_GOALS_3_5:
                return "OVER_GOALS_3_5";
            case MatchOddsMarketEnum.OVER_GOALS_4_5:
                return "OVER_GOALS_4_5";
            case MatchOddsMarketEnum.OVER_GOALS_5_5:
                return "OVER_GOALS_5_5";
            default:
                return null;
        }
    }

    // Handle API object format - match on code (uppercase)
    if (typeof marketType === "object" && marketType !== null) {
        const code = marketType.code?.toUpperCase();

        switch (code) {
            case "1X2":
            case "FT_1X2":
                return "FT_1X2";
            case "DOUBLE_CHANCE":
                return "DOUBLE_CHANCE";
            case "HT_FT":
            case "FIRST_HALF_AND_FINAL_RESULT":
                return "HT_FT";
            case "BOTH_TO_SCORE":
            case "BOTH_TEAMS_SCORE":
                return "BOTH_TEAMS_SCORE";
            case "CORRECT_SCORE":
                return "CORRECT_SCORE";
            case "PLAYER_SCORE_FIRST_GOAL":
                return "PLAYER_SCORE_FIRST_GOAL";
            case "PLAYER_SCORE":
                return "PLAYER_SCORE";
            case "PLAYER_RED_CARD":
                return "PLAYER_RED_CARD";
            case "PLAYER_YELLOW_CARD":
                return "PLAYER_YELLOW_CARD";
            case "OVER_UNDER":
            case "OVER_GOALS_2_5":
                return "OVER_GOALS_2_5";
            case "OVER_GOALS_0_5":
                return "OVER_GOALS_0_5";
            case "OVER_GOALS_1_5":
                return "OVER_GOALS_1_5";
            case "OVER_GOALS_3_5":
                return "OVER_GOALS_3_5";
            case "OVER_GOALS_4_5":
                return "OVER_GOALS_4_5";
            case "OVER_GOALS_5_5":
                return "OVER_GOALS_5_5";
            default:
                return null;
        }
    }

    return null;
}
